import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useTokenValidation } from '@/hooks/useTokenValidation';

export type UserSegment = "school_student" | "college_student" | "working_professional";

export interface UserProfile {
  id: string;
  email: string;
  full_name: string | null;
  career_stage: string | null;
  career_interests: string[] | null;
  custom_career_interests: string[] | null;
  current_job_title: string | null;
  years_experience: number | null;
  education_level: string | null;
  field_of_study: string | null;
  technical_skills: string[] | null;
  behavioral_skills: string[] | null;
  career_goals_list: string[] | null;
  skills_to_develop: string[] | null;
  subscription_tier: string | null;
  subscription_status: string | null;
  payment_status: string | null;
  profile_completion_percentage: number | null;
  onboarding_completed: boolean | null;
  is_admin: boolean | null;
  user_segment: UserSegment | null;
}

interface UserProfileContextType {
  profile: UserProfile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  refreshProfile: () => Promise<void>;
  updateProfileField: (field: keyof UserProfile, value: any) => void;
  getAllInterests: () => string[];
  isPro: boolean;
}

const UserProfileContext = createContext<UserProfileContextType | null>(null);

export const UserProfileProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { result } = useTokenValidation();
  const hasFetched = useRef(false);
  const lastEmail = useRef<string | null>(null);

  const fetchProfile = useCallback(async (email: string) => {
    try {
      console.log('[UserProfileContext] Fetching profile for:', email);
      
      const token = localStorage.getItem('user_auth_token') || 
                    localStorage.getItem('admin_auth_token');

      const { data, error } = await supabase.functions.invoke('get-user-profile', {
        body: { token, email }
      });

      if (error) {
        console.error('[UserProfileContext] Error fetching profile:', error);
        return;
      }

      if (data?.profile) {
        const profileData: UserProfile = {
          id: data.profile.id,
          email: data.profile.email,
          full_name: data.profile.full_name,
          career_stage: data.profile.career_stage,
          career_interests: data.profile.career_interests,
          custom_career_interests: data.profile.custom_career_interests,
          current_job_title: data.profile.current_job_title,
          years_experience: data.profile.years_experience,
          education_level: data.profile.education_level,
          field_of_study: data.profile.field_of_study,
          technical_skills: data.profile.technical_skills,
          behavioral_skills: data.profile.behavioral_skills,
          career_goals_list: data.profile.career_goals_list,
          skills_to_develop: data.profile.skills_to_develop,
          subscription_tier: data.profile.subscription_tier,
          subscription_status: data.profile.subscription_status,
          payment_status: data.profile.payment_status,
          profile_completion_percentage: data.profile.profile_completion_percentage,
          onboarding_completed: data.profile.onboarding_completed,
          is_admin: data.profile.is_admin,
          user_segment: data.profile.user_segment || null,
        };
        
        setProfile(profileData);
        console.log('[UserProfileContext] Profile loaded:', profileData.email);
        
        // Cache in sessionStorage for quick access
        sessionStorage.setItem('user_profile_cache', JSON.stringify(profileData));
      }
    } catch (error) {
      console.error('[UserProfileContext] Error:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load profile when authentication changes
  useEffect(() => {
    const email = result?.userEmail;
    
    if (!email) {
      // Quick check: if no tokens exist at all, skip cache lookup too
      const hasToken = localStorage.getItem('user_auth_token') || localStorage.getItem('admin_auth_token');
      if (hasToken) {
        // Try to load from cache for quick initial render
        const cached = sessionStorage.getItem('user_profile_cache');
        if (cached) {
          try {
            setProfile(JSON.parse(cached));
          } catch (e) {
            console.error('[UserProfileContext] Cache parse error:', e);
          }
        }
      }
      setIsLoading(false);
      return;
    }

    // Avoid duplicate fetches for same email
    if (email === lastEmail.current && hasFetched.current) {
      return;
    }

    lastEmail.current = email;
    hasFetched.current = true;
    fetchProfile(email);
  }, [result?.userEmail, fetchProfile]);

  // Subscribe to realtime profile updates
  useEffect(() => {
    if (!profile?.id) return;

    const channel = supabase
      .channel(`profile-${profile.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_profiles',
          filter: `id=eq.${profile.id}`,
        },
        (payload) => {
          console.log('[UserProfileContext] Profile updated via realtime:', payload);
          // Merge updates
          setProfile(prev => prev ? { ...prev, ...payload.new } : null);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [profile?.id]);

  const refreshProfile = useCallback(async () => {
    const email = result?.userEmail || profile?.email;
    if (email) {
      hasFetched.current = false;
      await fetchProfile(email);
    }
  }, [result?.userEmail, profile?.email, fetchProfile]);

  const updateProfileField = useCallback((field: keyof UserProfile, value: any) => {
    setProfile(prev => prev ? { ...prev, [field]: value } : null);
  }, []);

  // Combine all interest sources for personalization
  const getAllInterests = useCallback((): string[] => {
    if (!profile) return [];
    
    const interests: string[] = [];
    
    if (profile.career_interests) {
      interests.push(...profile.career_interests);
    }
    if (profile.custom_career_interests) {
      interests.push(...profile.custom_career_interests);
    }
    if (profile.current_job_title) {
      interests.push(profile.current_job_title);
    }
    if (profile.field_of_study) {
      interests.push(profile.field_of_study);
    }
    
    // Deduplicate
    return [...new Set(interests)];
  }, [profile]);

  const isPro = profile?.subscription_tier === 'pro' || 
                profile?.payment_status === 'paid' ||
                profile?.is_admin === true;

  const isAuthenticated = !!result?.isValid && !!profile;

  // Memoize context value to prevent downstream re-renders when parent re-renders
  const value = useMemo(
    () => ({
      profile,
      isLoading,
      isAuthenticated,
      refreshProfile,
      updateProfileField,
      getAllInterests,
      isPro,
    }),
    [profile, isLoading, isAuthenticated, refreshProfile, updateProfileField, getAllInterests, isPro]
  );

  return (
    <UserProfileContext.Provider value={value}>
      {children}
    </UserProfileContext.Provider>
  );
};

export const useProfile = () => {
  const context = useContext(UserProfileContext);
  if (!context) {
    throw new Error('useProfile must be used within a UserProfileProvider');
  }
  return context;
};

// Safe hook that doesn't throw if used outside provider
export const useProfileSafe = () => {
  const context = useContext(UserProfileContext);
  return context || {
    profile: null,
    isLoading: false,
    isAuthenticated: false,
    refreshProfile: async () => {},
    updateProfileField: () => {},
    getAllInterests: () => [],
    isPro: false,
  };
};
